// DataTable — a thin, generic, dense table over the shadcn table primitives.
//
// Columns declare a header, an accessor (cell render), optional alignment, and
// className. This keeps the screen files declarative while reusing the repo's
// real Table components (so styling/tokens stay consistent). Money cells should
// render <MoneyCell/> from the column's `cell`.

import { cn } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export interface Column<T> {
  key: string;
  header: React.ReactNode;
  cell: (row: T, index: number) => React.ReactNode;
  align?: "left" | "right" | "center";
  className?: string;
  headClassName?: string;
}

const alignClass = {
  left: "text-left",
  right: "text-right",
  center: "text-center",
} as const;

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  empty = "Nothing to show.",
  onRowClick,
  className,
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T, index: number) => string;
  empty?: React.ReactNode;
  onRowClick?: (row: T) => void;
  className?: string;
}) {
  if (rows.length === 0) {
    return <p className="py-8 text-center text-sm text-nx-text-3">{empty}</p>;
  }
  return (
    <Table className={className}>
      <TableHeader>
        <TableRow>
          {columns.map((c) => (
            <TableHead
              key={c.key}
              className={cn(
                c.align && alignClass[c.align],
                "text-xs font-medium tracking-wide text-nx-text-2 uppercase",
                c.headClassName,
              )}
            >
              {c.header}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row, i) => (
          <TableRow
            key={rowKey(row, i)}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            className={cn(onRowClick && "cursor-pointer")}
          >
            {columns.map((c) => (
              <TableCell
                key={c.key}
                className={cn(c.align && alignClass[c.align], c.className)}
              >
                {c.cell(row, i)}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
